import { Field } from './Field.js';
import { EmailField } from './EmailField.js';
import { checkEmail } from './emailValidation.js';
import type { WizardStep } from './RegistrationWizard.js';
import { learnerCoreSteps } from './learnerSteps.js';
import type { RegFormData } from './types.js';

const GUARDIAN_CONSENTS = [
  { key: 'recording', label: 'I consent to session recording and monitoring.' },
  { key: 'responsibility', label: 'I accept responsibility for the minor’s use and payment.' },
  { key: 'revoke', label: 'I understand I may revoke this consent at any time.' },
];

const guardianInfo: WizardStep = {
  key: 'guardian-info', title: 'Parent / guardian info',
  validate: (d) => {
    const e: Record<string, string> = {};
    if (!d.guardianName?.trim()) e.guardianName = 'Required';
    if (!d.guardianEmail?.trim()) e.guardianEmail = 'Required';
    else {
      const msg = checkEmail(d.guardianEmail).error;
      if (msg) e.guardianEmail = msg;
    }
    if (!d.guardianRelationship?.trim()) e.guardianRelationship = 'Required';
    return e;
  },
  Component: ({ data, update, errors }) => (
    <>
      <Field label="Parent/guardian full name" error={errors.guardianName}>
        <input className="input" value={data.guardianName ?? ''} onChange={(e) => update({ guardianName: e.target.value })} />
      </Field>
      <Field label="Relationship to minor" error={errors.guardianRelationship}>
        <input className="input" value={data.guardianRelationship ?? ''} onChange={(e) => update({ guardianRelationship: e.target.value })} />
      </Field>
      <EmailField label="Parent/guardian email" value={data.guardianEmail ?? ''}
        error={errors.guardianEmail} onChange={(v) => update({ guardianEmail: v })} />
      <Field label="Parent/guardian phone">
        <input className="input" value={data.guardianPhone ?? ''} onChange={(e) => update({ guardianPhone: e.target.value })} />
      </Field>
      <Field label="Mailing address">
        <textarea className="input" rows={2} value={data.guardianMailingAddress ?? ''} onChange={(e) => update({ guardianMailingAddress: e.target.value })} />
      </Field>
    </>
  ),
};

const guardianConsent: WizardStep = {
  key: 'guardian-consent', title: 'Parent / guardian consent',
  validate: (d) => {
    const e: Record<string, string> = {};
    const c = d.guardianConsents ?? {};
    if (!GUARDIAN_CONSENTS.every((g) => c[g.key])) e.guardianConsents = 'All consents are required';
    if (!d.guardianSignature?.trim()) e.guardianSignature = 'Type your name to sign';
    return e;
  },
  Component: ({ data, update, errors }) => {
    const c = data.guardianConsents ?? {};
    const set = (k: string, v: boolean) => update({ guardianConsents: { ...c, [k]: v } });
    return (
      <>
        {GUARDIAN_CONSENTS.map((g) => (
          <label key={g.key} className="inline-flex items-start gap-2">
            <input type="checkbox" checked={c[g.key] ?? false} onChange={(e) => set(g.key, e.target.checked)} />
            <span className="hint">{g.label}</span>
          </label>
        ))}
        {errors.guardianConsents ? <span className="text-[0.8rem] text-status-error dark:text-red-400">{errors.guardianConsents}</span> : null}
        <Field label="Parent/guardian e-signature" hint="Placeholder. Verification link to the guardian comes later." error={errors.guardianSignature}>
          <input className="input" value={data.guardianSignature ?? ''} onChange={(e) => update({ guardianSignature: e.target.value })} />
        </Field>
        <Field label="Minor signature (optional)">
          <input className="input" value={data.minorSignature ?? ''} onChange={(e) => update({ minorSignature: e.target.value })} />
        </Field>
      </>
    );
  },
};

/** 3 core learner steps, with the 2 guardian steps spliced in before the final
 *  Plan & Agreement step when the learner selected the 14-17 age range. */
export function learnerGetSteps(data: RegFormData): WizardStep[] {
  if (data.ageRange !== '14-17') return learnerCoreSteps;
  const last = learnerCoreSteps[learnerCoreSteps.length - 1];
  const head = learnerCoreSteps.slice(0, -1);
  return [...head, guardianInfo, guardianConsent, last];
}
